import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

// Import Bootstrap and styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './styles/App.css';

// Context Providers
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { UserProvider } from './context/UserContext';

// Components
import Navbar from './components/common/Navbar';
import MobileNavbar from './components/common/MobileNavbar';
import Footer from './components/common/Footer';
import CartSidebar from './components/common/CartSidebar';

// Pages
import Home from './pages/Home';
import Login from './pages/auth/Login';
import About from './pages/About';
import Contact from './pages/Contact';
import MyReferral from './pages/user/MyReferral';
import MyTeam from './pages/user/MyTeam';
import LevelIncome from './pages/user/LevelIncome';
import UserRank from './pages/user/UserRank';
import DashboardLayout from './pages/user/Dashboardlayout';
import DashboardCards from './pages/user/DasboardUser';

import SettingPasswors from './pages/user/SettingPassword';
import Transafers from './pages/user/Transafers';

// Lazy loaded pages
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const Collections = lazy(() => import('./pages/Collections'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Profile = lazy(() => import('./pages/user/Profile'));
const Orders = lazy(() => import('./pages/user/Orders'));
const Wishlist = lazy(() => import('./pages/Wishlist'));

// Loading Component
const LoadingScreen = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    <div className="text-center">
      <div className="spinner-border" role="status" style={{
        width: '3rem',
        height: '3rem',
        color: 'var(--primary-gold)'
      }}>
        <span className="visually-hidden">Loading...</span>
      </div>
      <h5 className="mt-3 gold-text">Loading...</h5>
    </div>
  </div>
);

// Dashboard paths — inpe Navbar/Footer hide rahega
const DASHBOARD_PATHS = [
  '/dashboard-user',
  '/profile',
  '/my-referral',
  '/my-team',
  '/level-income',
  '/user-rank',
  '/password-settings',
  '/transfer'
];

// ── Main content wrapper (useLocation Router ke andar chahiye) ──
function AppContent() {
  const location = useLocation();
  const isDashboard = DASHBOARD_PATHS.some(p => location.pathname.startsWith(p));

  return (
    <div className="App">

      {/* Navbar — dashboard pe hide */}
      {!isDashboard && (
        <div className="d-none d-lg-block">
          <Navbar />
        </div>
      )}
      {!isDashboard && <MobileNavbar />}

      {/* Main content */}
      <main
        className="main-content"
        style={{
          minHeight: 'calc(100vh - 200px)',
          paddingBottom: isDashboard ? 0 : '80px',
        }}
      >
        <Suspense fallback={<LoadingScreen />}>
          <Routes>

            {/* ── Public Routes ── */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register/:referralCode" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/category/:category" element={<Collections />} />
            <Route path="/collections/:collection" element={<Collections />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/orders" element={<Orders />} />

            {/* ── Dashboard Routes — Navbar/Footer se bilkul alag ── */}
            <Route path="/dashboard-user" element={<DashboardLayout />}>
              <Route index element={<DashboardCards />} />
            </Route>
            <Route path="/profile" element={<DashboardLayout />}>
              <Route index element={<Profile />} />
            </Route>
            <Route path="/my-referral" element={<DashboardLayout />}>
              <Route index element={<MyReferral />} />
            </Route>
            <Route path="/my-team" element={<DashboardLayout />}>
              <Route index element={<MyTeam />} />
            </Route>
            <Route path="/level-income" element={<DashboardLayout />}>
              <Route index element={<LevelIncome />} />
            </Route>
            <Route path="/password-settings" element={<DashboardLayout />}>
              <Route index element={<SettingPasswors />} />
            </Route>

            <Route path="/user-rank" element={<DashboardLayout />}>
              <Route index element={<UserRank />} />
            </Route>
            
            <Route path="/transfer" element={<DashboardLayout />}>
              <Route index element={<Transafers />} />
            </Route>

            {/* ── 404 ── */}
            <Route path="*" element={
              <div className="container text-center py-5">
                <h1 className="display-1 gold-text">404</h1>
                <h3>Page Not Found</h3>
                <p>The page you're looking for doesn't exist.</p>
                <a href="/" className="btn btn-luxury">Go Home</a>
              </div>
            } />

          </Routes>
        </Suspense>
      </main>

      {/* Footer & CartSidebar — dashboard pe hide */}
      {!isDashboard && <Footer />}
      {!isDashboard && <CartSidebar />}

    </div>
  );
}

// ── Root App ──
function App() {
  useEffect(() => {
    console.log('Bharat Gold Jewellery - Luxury E-Commerce Platform');
  }, []);

  return (
    <ThemeProvider>
      <UserProvider>
        <CartProvider>
          <Router>
            <AppContent />
            <ToastContainer
              position="bottom-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </Router>
        </CartProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;